/**
 * StatCard — compact metric tile (label + big numeric value + optional tone/icon).
 *
 * Tablet-first: minimum 96px tall, numeric value uses JetBrains Mono for
 * tabular-aligned reading. Tone is color + icon (never color-only).
 *
 * Intended for admin dashboards (migrations, policies) and business KPIs.
 */
import {
  CheckCircle,
  ClockCounterClockwise,
  Minus,
  PauseCircle,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { cn } from '../utils/cn';

export type StatCardTone =
  | 'neutral'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'paused';

interface ToneDef {
  surface: string;
  value: string;
  icon: ReactNode;
}

const TONE_MAP: Record<StatCardTone, ToneDef> = {
  neutral: {
    surface: 'bg-biz-surface-elevated border-biz-border',
    value: 'text-biz-foreground',
    icon: <Minus weight="regular" size={18} aria-hidden="true" className="text-biz-muted-foreground" />,
  },
  success: {
    surface: 'bg-biz-success-subtle border-biz-success/30',
    value: 'text-biz-success',
    icon: <CheckCircle weight="regular" size={18} aria-hidden="true" className="text-biz-success" />,
  },
  info: {
    surface: 'bg-biz-info-subtle border-biz-info/30',
    value: 'text-biz-info',
    icon: <ClockCounterClockwise weight="regular" size={18} aria-hidden="true" className="text-biz-info" />,
  },
  warning: {
    surface: 'bg-biz-warning-subtle border-biz-warning/30',
    value: 'text-biz-warning',
    icon: <Warning weight="regular" size={18} aria-hidden="true" className="text-biz-warning" />,
  },
  danger: {
    surface: 'bg-biz-danger-subtle border-biz-danger/30',
    value: 'text-biz-danger',
    icon: <XCircle weight="regular" size={18} aria-hidden="true" className="text-biz-danger" />,
  },
  paused: {
    surface: 'bg-biz-muted border-biz-border',
    value: 'text-biz-muted-foreground',
    icon: <PauseCircle weight="regular" size={18} aria-hidden="true" className="text-biz-muted-foreground" />,
  },
};

export interface StatCardProps {
  label: string;
  value: number | string;
  tone?: StatCardTone;
  hint?: string;
  className?: string;
  /** Override the default tone icon. */
  icon?: ReactNode;
}

export function StatCard({
  label,
  value,
  tone = 'neutral',
  hint,
  className,
  icon,
}: StatCardProps): JSX.Element {
  const def = TONE_MAP[tone];
  const displayValue =
    typeof value === 'number' ? new Intl.NumberFormat().format(value) : value;
  return (
    <div
      className={cn(
        'relative min-h-[96px]',
        'px-biz-card py-biz-content-lg',
        'border rounded-biz-card',
        'flex flex-col gap-biz-content',
        def.surface,
        className,
      )}
    >
      <div className="flex items-center gap-biz-content text-biz-xs font-medium text-biz-muted-foreground uppercase tracking-wider">
        {icon ?? def.icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={cn('text-biz-3xl font-bold biz-numeric leading-none', def.value)}>
        {displayValue}
      </div>
      {hint ? (
        <div className="text-biz-xs text-biz-muted-foreground">{hint}</div>
      ) : null}
    </div>
  );
}
